import { getFailureTypeInsightsName } from "@/lib/failedInvoiceTypes";
import { INVOICE_CATEGORY } from "@/lib/invoiceCategories";
import { PROCESSING_STATUS } from "@/lib/statusEnums";
import { OVERVIEW_STAT_COLORS } from "@/lib/semanticColors";

/**
 * Every overview tile. Each one is a filter and a count at once:
 *   - the filter is `chip` (a processing status), `category` (sets `CategoryId`)
 *     or `failureType` (sets `FailureType`), so a click narrows the list the same
 *     way wherever it is clicked from;
 *   - the count is `source`, the list and item in the insights response that
 *     answers "how many rows would this click show".
 */
/**
 * A failure-type tile. The filter value and the insights name come from the same
 * `failureType` argument, so the tile cannot filter by one type and count another.
 */
function failureTypeStat(key, failureType, label, color) {
  return {
    key,
    failureType,
    source: ["failureType", getFailureTypeInsightsName(failureType)],
    label,
    color,
  };
}

const STAT = {
  underReview: {
    key: "underReview",
    chip: "review",
    source: ["processingStatus", PROCESSING_STATUS.UNDER_REVIEW],
    label: "Under Review",
    color: OVERVIEW_STAT_COLORS.review,
  },
  failed: {
    key: "failed",
    chip: "failed",
    source: ["processingStatus", PROCESSING_STATUS.FAILED],
    label: "Failed",
    color: OVERVIEW_STAT_COLORS.failed,
  },
  successful: {
    key: "successful",
    chip: "successful",
    source: ["processingStatus", PROCESSING_STATUS.SUCCESSFUL],
    label: "Successful",
    color: OVERVIEW_STAT_COLORS.successful,
  },
  invoices: {
    key: "invoices",
    category: INVOICE_CATEGORY.INVOICE,
    source: ["category", INVOICE_CATEGORY.INVOICE],
    label: "Invoices",
    color: OVERVIEW_STAT_COLORS.invoices,
  },
  samples: {
    key: "samples",
    category: INVOICE_CATEGORY.SAMPLE_INVOICE,
    source: ["category", INVOICE_CATEGORY.SAMPLE_INVOICE],
    label: "Samples",
    color: OVERVIEW_STAT_COLORS.samples,
  },
  creditNotesCategory: {
    key: "creditNotesCategory",
    category: INVOICE_CATEGORY.CREDIT_NOTE,
    source: ["category", INVOICE_CATEGORY.CREDIT_NOTE],
    label: "Credit Notes (Category)",
    color: OVERVIEW_STAT_COLORS.creditNotesCategory,
  },
  noPo: failureTypeStat("noPo", "No PO", "No PO", OVERVIEW_STAT_COLORS.noPo),
  foreignExchange: failureTypeStat("foreignExchange", "Foreign Exchange", "Foreign Exchange", OVERVIEW_STAT_COLORS.foreignExchange),
  creditNotesFailure: failureTypeStat("creditNotesFailure", "Credit Note", "Credit Notes (Failure type)", OVERVIEW_STAT_COLORS.creditNotesFailure),
  priceDifference: failureTypeStat("priceDifference", "Price mismatch", "Price Difference", OVERVIEW_STAT_COLORS.priceDifference),
  vatMismatch: failureTypeStat("vatMismatch", "VAT mismatch", "VAT mismatch", OVERVIEW_STAT_COLORS.vatMismatch),
};

/** Invoices and My Invoices: the same seven tiles on both. */
export const QUEUE_OVERVIEW_STATS = [
  STAT.underReview,
  STAT.samples,
  STAT.creditNotesCategory,
  STAT.creditNotesFailure,
  STAT.foreignExchange,
  STAT.failed,
  STAT.successful,
];

/**
 * Failed and In Query already fix the processing status, so status tiles would
 * only ever read 0. They count by category and failure type instead.
 */
export const EXCEPTION_OVERVIEW_STATS = [
  STAT.invoices,
  STAT.creditNotesCategory,
  STAT.samples,
  STAT.noPo,
  STAT.foreignExchange,
  STAT.creditNotesFailure,
  STAT.priceDifference,
  STAT.vatMismatch,
];

/** The tiles a queue shows, from its `overview` setting. */
export function getOverviewStats(config) {
  return config.overview === "exceptions" ? EXCEPTION_OVERVIEW_STATS : QUEUE_OVERVIEW_STATS;
}

/** The tile for a filter value (`field` is "chip", "category" or "failureType"). */
export function findOverviewStat(field, value) {
  return Object.values(STAT).find((stat) => stat[field] === value);
}

/** Whether `stat`'s own filter dimension is the one currently applied. */
export function isOverviewStatSelected(stat, { queueChip, categoryFilter, failureTypeFilter }) {
  if (stat.chip) return queueChip === stat.chip;
  if (stat.category != null) return categoryFilter === stat.category;
  if (stat.failureType) return failureTypeFilter === stat.failureType;
  return false;
}

/**
 * Toggles `stat`'s own filter dimension — clicking a second time clears just
 * that dimension, leaving the other two untouched.
 */
export function selectOverviewStat(
  stat,
  { queueChip, categoryFilter, failureTypeFilter, onSelectChip, onSelectCategory, onSelectFailureType },
) {
  const selected = isOverviewStatSelected(stat, { queueChip, categoryFilter, failureTypeFilter });
  if (stat.chip) return onSelectChip?.(selected ? "all" : stat.chip);
  if (stat.category != null) return onSelectCategory?.(selected ? "" : stat.category);
  if (stat.failureType) return onSelectFailureType?.(selected ? "" : stat.failureType);
}
